import { usePatientDiagnosticReports } from '@ctw/shared/api/fhir/diagnostic-report';
import type { DiagnosticReportModel } from '@ctw/shared/api/fhir/models/diagnostic-report';
import { useDiagnosticRowActions } from '@ctw/shared/content/diagnostic-reports/helpers/row-actions';
import {
  ResourceOverviewCard,
  type SpecificResourceOverviewCardProps,
} from '@ctw/shared/content/overview/resource-overview-card';
import { withOverviewCardErrorBoundary } from '@ctw/shared/content/overview/with-overview-card-error-boundary';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { usePatientContext } from '@ctw/shared/context/patient-provider';
import { useResourceDrawer } from '@ctw/shared/context/resource-drawer-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { useHotloadReady } from '@ctw/shared/hooks/use-hotload-ready';
import { getBffApiBaseUrl } from '@ctw/shared/utils/environments';
import { sort } from '@ctw/shared/utils/sort';
import { tw } from '@ctw/shared/utils/tailwind';
import { faFlask } from '@fortawesome/pro-solid-svg-icons';
import { compact } from 'lodash-es';
import { useEffect, useState } from 'react';
import type { OverviewFilter } from '../overview/filters';
import { RenderSyncedWithRecordIcon, ResourceTitleColumn } from '../resource/resource-title-column';

const TITLE = 'Select Labs';

const hasA1CCode = (A1CCodes: Array<string>, r: DiagnosticReportModel) =>
  r.loincCode?.code && A1CCodes.includes(r.loincCode.code);

const containsText = (r: DiagnosticReportModel, text: string) =>
  r.displayName.toLowerCase().includes(text);

const cleanupA1CDisplay = (A1CCodes: Array<string>, r: DiagnosticReportModel) => {
  // First check that it is a 1AC display
  if (hasA1CCode(A1CCodes, r) || containsText(r, 'a1c')) {
    if (hasA1CCode(A1CCodes, r)) {
      return r.displayName;
    }
    return 'A1c';
  }

  // If not an a1c display don't manipulate text
  return r.displayName;
};

export const getOverviewFilters = (
  A1CCodes: Array<string>,
): Array<OverviewFilter<DiagnosticReportModel>> => [
  {
    key: 'a1c',
    label: 'Filter by A1C',
    description: 'Contains a1c in text or if it includes an a1c code',
    predicate: (r: DiagnosticReportModel) => hasA1CCode(A1CCodes, r) || containsText(r, 'a1c'),
  },
  {
    key: 'cbc',
    label: 'Filter by CBC',
    description: 'Match lowercase “cbc” or “complete blood count” but not containing “cd4/cd8”',
    predicate: (r: DiagnosticReportModel) =>
      /cbc(?!.*cd4\/cd8)|complete blood count/.test(r.displayName.toLowerCase()),
  },
  {
    key: 'cmp',
    label: 'Filter by CMP',
    description: 'Match lowercase “cmp” or “bmp” or “metabolic panel”',
    predicate: (r: DiagnosticReportModel) =>
      ['cmp', 'bmp', 'metabolic panel'].includes(r.displayName.toLowerCase()),
  },
];

type ValueSetResource = {
  id: string;
  type: 'value-set';
  attributes: {
    display: string;
    content: Array<ValueSet>;
  };
};

type ValueSetResponse = {
  data: ValueSetResource;
};

const useA1CCodes = () =>
  useCtwQuery<{ content: Array<ValueSet> }>({
    queryId: 'a1cCodes',
    queryFn: async ({ requestContext, ctwFetch }) => {
      const { data } = await ctwFetch<ValueSetResponse>(
        `${getBffApiBaseUrl(requestContext.env)}/v1/terminology/valuesets/a1c`,
        {
          headers: {
            Authorization: `Bearer ${requestContext.authToken}`,
            'content-type': 'application/json',
          },
        },
      );
      return { content: data?.data?.attributes?.content ?? [] };
    },
  });

type ValueSet = {
  code: string;
  display: string;
};

export const PatientDiagnosticReportsOverview = withOverviewCardErrorBoundary({
  cardTitle: TITLE,
  boundaryName: 'PatientDiagnosticReportsOverview',
  Component: ({ onSeeAllResources }: SpecificResourceOverviewCardProps) => {
    const [A1CCodes, setA1CCodes] = useState<Array<string>>([]);
    const [mergedData, setMergedData] = useState<Array<DiagnosticReportModel>>([]);
    const resourceActions = useDiagnosticRowActions();
    const { openResourceDrawer } = useResourceDrawer({
      enableDismissAndReadActions: false,
      resourceActions: resourceActions as never,
    });
    const { patient } = usePatientContext();
    // Defer the query until diagnostic reports are hotloaded (gated behind `ctw-hotload-blocking`),
    // so the card renders its chrome with a body spinner rather than blocking inline on ODS.
    const ready = useHotloadReady({ upid: patient.UPID, resourceTypes: ['DiagnosticReport'] });
    const { data, isLoading } = usePatientDiagnosticReports(undefined, ready);
    const { requestContext } = useCTW();
    const useA1CCodesResponse = useA1CCodes();

    const loading = !ready || isLoading || useA1CCodesResponse.isLoading;
    const hasData = Boolean(data && data.length > 0);

    useEffect(() => {
      if (useA1CCodesResponse.data?.content) {
        setA1CCodes(useA1CCodesResponse.data.content.map((item: ValueSet) => item.code));
      }
    }, [useA1CCodesResponse.data]);

    // Wait for the A1C codes to load before rendering the data
    useEffect(() => {
      if (A1CCodes.length > 0) {
        const filteredData = compact(
          getOverviewFilters(A1CCodes).flatMap(
            (filter) => sort(data ?? [], 'date', 'desc', true)?.filter(filter.predicate)[0],
          ),
        );
        setMergedData(filteredData);
      }
    }, [A1CCodes, data]);

    return (
      <ResourceOverviewCard
        onRowClick={({ telemetry, record }) => openResourceDrawer({ telemetry, model: record })}
        data={mergedData}
        emptyState={
          !hasData
            ? "We didn't find any lab records for this patient."
            : 'There are no metabolic or CBC panel and A1C labs for this patient.'
        }
        footerCTA={
          loading || hasData
            ? {
                label: 'All Labs',
                onClick: onSeeAllResources,
              }
            : undefined
        }
        title={TITLE}
        helpText="Most recent Metabolic or CBC Panel and A1C"
        loading={loading}
        headerIcon={faFlask}
        testId="labs-overview"
        renderResource={(record: DiagnosticReportModel) => (
          <div>
            <ResourceTitleColumn
              title={cleanupA1CDisplay(A1CCodes, record)}
              renderIcon={RenderSyncedWithRecordIcon(
                record.ownedByBuilder(requestContext.builderId),
              )}
            />
            <div className={tw`font-normal`}>Recorded: {record.dateDisplay}</div>
          </div>
        )}
      />
    );
  },
});
